/**
 * Máscaras de digitação para os documentos brasileiros.
 *
 * Aplicadas enquanto o usuário digita; o valor enviado à API vai sem máscara
 * — o backend também aceita os dois formatos, mas mandar limpo evita depender
 * disso.
 */

export function apenasDigitos(valor: string): string {
  return valor.replace(/\D/g, '');
}

/** 000.000.000-00 */
export function mascararCpf(valor: string): string {
  const d = apenasDigitos(valor).slice(0, 11);

  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
}

/** 000 0000 0000 0000 */
export function mascararCns(valor: string): string {
  const d = apenasDigitos(valor).slice(0, 15);

  return d
    .replace(/^(\d{3})(\d)/, '$1 $2')
    .replace(/^(\d{3}) (\d{4})(\d)/, '$1 $2 $3')
    .replace(/^(\d{3}) (\d{4}) (\d{4})(\d)/, '$1 $2 $3 $4');
}

/** (00) 0000-0000 para fixo, (00) 90000-0000 para celular. */
export function mascararTelefone(valor: string): string {
  const d = apenasDigitos(valor).slice(0, 11);

  if (d.length <= 10) {
    return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d{1,4})$/, '$1-$2');
  }

  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d{1,4})$/, '$1-$2');
}

/**
 * Valida CPF pelo módulo 11 — espelha `backend/src/common/validadores/documentos.ts`.
 * Validar aqui é conveniência: quem decide é o servidor.
 */
export function cpfValido(valor: string): boolean {
  const d = apenasDigitos(valor);

  if (d.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(d)) return false;

  const digito = (ate: number): number => {
    let soma = 0;
    for (let i = 0; i < ate; i += 1) soma += Number(d[i]) * (ate + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };

  return digito(9) === Number(d[9]) && digito(10) === Number(d[10]);
}

/** Valida o Cartão Nacional de Saúde — espelha o validador do backend. */
export function cnsValido(valor: string): boolean {
  const d = apenasDigitos(valor);

  if (d.length !== 15) return false;
  if (/^(\d)\1{14}$/.test(d)) return false;

  const inicial = d[0];

  if (inicial === '1' || inicial === '2') {
    let soma = 0;
    for (let i = 0; i < 11; i += 1) soma += Number(d[i]) * (15 - i);

    let verificador = 11 - (soma % 11);
    if (verificador === 11) verificador = 0;

    if (verificador === 10) {
      soma += 2;
      verificador = 11 - (soma % 11);
      return d.slice(11) === `001${verificador}`;
    }

    return d.slice(11) === `000${verificador}`;
  }

  if (inicial === '7' || inicial === '8' || inicial === '9') {
    let soma = 0;
    for (let i = 0; i < 15; i += 1) soma += Number(d[i]) * (15 - i);
    return soma % 11 === 0;
  }

  return false;
}

/** Idade em anos completos, para exibir ao lado da data de nascimento. */
export function calcularIdade(dataNascimento: string | Date): number | null {
  const nascimento = typeof dataNascimento === 'string' ? new Date(dataNascimento) : dataNascimento;
  if (Number.isNaN(nascimento.getTime())) return null;

  const hoje = new Date();
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const mes = hoje.getMonth() - nascimento.getMonth();

  if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) idade -= 1;

  return idade >= 0 ? idade : null;
}

/** Formata bytes de forma legível. */
export function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Converte para ASCII maiúsculo, removendo acentuação e cedilha.
 *
 * Espelha `backend/src/common/texto.ts`. Aplicado enquanto o operador digita
 * para que o que ele vê seja exatamente o que será gravado — sem a surpresa
 * de o servidor devolver um texto diferente do enviado.
 *
 * Vale só para os campos de identificação do paciente: nome, nome da mãe e
 * endereço. E-mail fica em minúsculas e observações preservam o texto.
 */
export function paraAsciiMaiusculo(valor: string): string {
  return valor
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\u00C6/g, 'AE')
    .replace(/\u0152/g, 'OE')
    .replace(/\u00D8/g, 'O')
    .replace(/\u00D0/g, 'D')
    .replace(/\u00DE/g, 'TH')
    .replace(/\u00DF/gi, 'SS')
    .replace(/[^A-Z0-9 '\-.,/]/g, '')
    .replace(/\s+/g, ' ');
}
